import React, { FC, useMemo, useState } from "react";
import HomePageTitle from "@components/Home/HomePageTitle/HomePageTitle";
import { useTranslation } from "react-i18next";
import { Box, Button, Drawer, Grid2 as Grid, IconButton, Skeleton, Stack, Typography } from "@mui/material";
import GamesCTATile from "@components/Home/Games/GamesCTATile";
import UpcomingScheduleTile from "@components/Home/UpcomingScheduleTile";
import DashboardTile from "@components/Home/DashboardTile/DashboardTile";
import { DateTime } from "luxon";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import ModeSwitch from "@components/ModeSwitch/ModeSwitch";
import styled from "@emotion/styled";
import DashboardWrapper from "@components/Home/DashboardWrapper";
import { ScreenSize } from "@constants/screen-size";
import { useNavigate } from "react-router-dom";
import { useGetCompanionsQuery } from "@store/companionApi";
import { useGetSessionsQuery } from "@store/sessionApi";
import { useGetAlbumsQuery } from "@store/albumApi";
import { Order } from "@models/page-options";
import { DateUtil } from "@utils/date.util";
import { ActivityType } from "@models/activity-type";
import { Album } from "@models/album";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import CloseIcon from "@mui/icons-material/Close";
import PhotoPlaceholder from "@components/PhotoPlaceholder";
import PhotoAlbumOutlinedIcon from "@mui/icons-material/PhotoAlbumOutlined";
import { DashboardClickableTileButton } from "@components/Home/DashboardClickableTile";

const PatientDashboardWrapper = styled(DashboardWrapper)`
  background-color: #faf8f5;

  @media (min-width: ${ScreenSize.md}px) {
    margin: 0 auto;
    max-width: 1024px;
  }
`;

const CompanionAvatar = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  object-position: top;
  border-radius: 12px;
`;

const AlbumCover = styled.img`
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 10px;
`;

// ─── Album picker drawer ──────────────────────────────────────────────────────

const AlbumPickerDrawer: FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const patientId = useSelector((state: RootState) => state.home.selectedPatientId);

  const { data: albums, isLoading } = useGetAlbumsQuery(
    { patientId: patientId! },
    { skip: !patientId },
  );

  const filteredAlbums = useMemo(
    () => albums?.data ?? [],
    [albums],
  );

  const onAlbumSelect = (album: Album) => {
    onClose();
    navigate("/portal/talk", {
      state: { albumId: album.id, activityType: ActivityType.GallerySession, isPatient: true },
    });
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          maxHeight: "75vh",
          px: 2,
          pb: 4,
        },
      }}
    >
      {/* Handle + header */}
      <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5, pb: 0.5 }}>
        <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: "divider" }} />
      </Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.5 }}>
        <Typography variant="h6" fontWeight={600}>
          {t("patientDashboard.companion.photoWalk")}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Stack>

      {/* Album grid */}
      {isLoading ? (
        <Grid container spacing={2}>
          {[1, 2, 3].map((i) => (
            <Grid key={i} size={{ xs: 6, sm: 4 }}>
              <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      ) : filteredAlbums.length === 0 ? (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography color="text.secondary">
            {t("patientDashboard.albums.noData")}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {filteredAlbums.map((album) => (
            <Grid key={album.id} size={{ xs: 6, sm: 4 }}>
              <DashboardClickableTileButton
                tileVariant="photoPicker"
                onClick={() => onAlbumSelect(album)}
              >
                <Box
                  sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  <Box sx={{ position: "relative" }}>
                    {album.coverPhoto ? (
                      <AlbumCover src={album.coverPhoto.fileUrl} alt={album.name} />
                    ) : (
                      <Box
                        sx={{
                          height: 120,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "action.hover",
                        }}
                      >
                        <PhotoPlaceholder width="60px" height="60px" />
                      </Box>
                    )}
                    <Box sx={{ position: "absolute", top: 8, right: 8 }}>
                      <Box
                        sx={{
                          bgcolor: "background.paper",
                          borderRadius: "50%",
                          p: 0.5,
                          boxShadow: 1,
                          display: "flex",
                          color: "primary.main",
                        }}
                      >
                        <PhotoAlbumOutlinedIcon fontSize="small" />
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ p: 1.25 }}>
                    <Typography fontWeight={600} fontSize="0.9375rem" noWrap>
                      {album.allPhotos
                        ? t("albums.allPhotosAlbumLabel")
                        : album.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("dashboard.albums.numberOfPhotos", {
                        photos: album.photoCount ?? 0,
                      })}
                    </Typography>
                  </Box>
                </Box>
              </DashboardClickableTileButton>
            </Grid>
          ))}
        </Grid>
      )}
    </Drawer>
  );
};

// ─── Companion card ───────────────────────────────────────────────────────────

const PatientCompanionCard: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const patientId = useSelector((state: RootState) => state.home.selectedPatientId);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data: companions } = useGetCompanionsQuery(
    { patientId: patientId ?? undefined, userId: undefined },
    { skip: !patientId },
  );

  const { data: sessions } = useGetSessionsQuery(
    { patientId: patientId!, order: Order.DESC, page: 1, pageSize: 1 },
    { skip: !patientId },
  );

  const companion = useMemo(
    () => companions?.data?.find(({ isDefault }) => isDefault),
    [companions],
  );

  const lastSession = sessions?.data?.[0];

  const lastSessionDate = useMemo(
    () => (lastSession ? DateUtil.formatTime(lastSession.createdDate) : t("never")),
    [lastSession, t],
  );

  if (!companion || !sessions) {
    return (
      <DashboardTile title=" ">
        <Stack spacing={2}>
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={52} sx={{ borderRadius: 2 }} />
        </Stack>
      </DashboardTile>
    );
  }

  return (
    <>
      <DashboardTile title={t("dashboard.companion.title")}>
        <Stack spacing={2.5}>
          <CompanionAvatar src={companion.avatar.imageUrl} alt={companion.name} />

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography fontSize="20px" fontWeight={600} color="text.secondary">
              {companion.name}
            </Typography>
            <Typography fontSize="14px" color="text.secondary">
              {t("dashboard.companion.lastEngaged", { date: lastSessionDate })}
            </Typography>
          </Stack>

          <Button
            variant="contained"
            color="success"
            size="large"
            fullWidth
            startIcon={<LocalPhoneOutlinedIcon />}
            onClick={() => navigate("/portal/talk")}
            id="patient-call-button"
          >
            {t("dashboard.companion.startCallButton", { name: companion.name })}
          </Button>

          <Button
            variant="outlined"
            size="large"
            fullWidth
            startIcon={<PhotoLibraryOutlinedIcon />}
            onClick={() => setDrawerOpen(true)}
          >
            {t("patientDashboard.companion.photoWalk")}
          </Button>
        </Stack>
      </DashboardTile>

      <AlbumPickerDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </>
  );
};

// ─── Patient dashboard ────────────────────────────────────────────────────────

const PatientDashboard: FC = () => {
  const { t } = useTranslation();
  const patient = useSelector((state: RootState) => state.home.selectedPatient);
  const isPatient = useSelector((state: RootState) => state.auth.user?.isPatient);

  const getGreeting = (): string => {
    const currentHour = DateTime.now().hour;
    let label;
    if (currentHour >= 3 && currentHour < 12) {
      label = "patientDashboard.title.goodMorning";
    } else if (currentHour >= 12 && currentHour < 15) {
      label = "patientDashboard.title.goodAfternoon";
    } else if (currentHour >= 15 && currentHour < 20) {
      label = "patientDashboard.title.goodEvening";
    } else {
      label = "patientDashboard.title.hello";
    }
    return t(label, {
      name: patient?.nickname ? patient?.nickname : patient?.firstName,
    });
  };

  return (
    <PatientDashboardWrapper>
      <Stack
        flexDirection="row"
        gap="16px"
        justifyContent="space-between"
        alignItems="center"
      >
        <HomePageTitle title={getGreeting()} />
        {isPatient ? null : (
          <Box width={{ xs: "140px", sm: "200px" }} marginBottom={4}>
            <ModeSwitch />
          </Box>
        )}
      </Stack>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <PatientCompanionCard />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <GamesCTATile basePath="portal" />
        </Grid>

        <Grid size={12}>
          <UpcomingScheduleTile />
        </Grid>
      </Grid>
    </PatientDashboardWrapper>
  );
};

export default PatientDashboard;
